import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
import { LuPlus } from "react-icons/lu";
import { CiTrash } from "react-icons/ci";
import axiosInstance from "../axiosinstance";
import "../Tables/Table.css";
import "./Forms.css";
import "./ChecklistTemplateForm.css";

const createEmptyItem = () => ({
  id: "",
  item_name: "",
  item_note: "",
});

const createEmptySection = () => ({
  id: "",
  name: "",
  requirement_type: "ALL_REQUIRED",
  section_note: "",
  items: [createEmptyItem()],
});

function ChecklistTemplateForm({ isSidebarExpanded }) {
  const { checklist_id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(checklist_id);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(isEditMode);
  const [detailError, setDetailError] = useState("");
  const [formData, setFormData] = useState({
    checklist_name: "",
    checklist_description: "",
    status: "publish",
    sections: [createEmptySection()],
  });
  const [errors, setErrors] = useState({
    checklist_name: "",
    sections: [],
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const statusMap = {
    publish: "publish",
    draft: "draft",
  };

  const pageTitle = useMemo(
    () => (isEditMode ? "Edit Checklist Template" : "Create Checklist Template"),
    [isEditMode]
  );

  const saveButtonLabel = useMemo(() => {
    if (isSaving) return isEditMode ? "Updating..." : "Saving...";
    return isEditMode ? "Update" : "Save";
  }, [isEditMode, isSaving]);

  useEffect(() => {
    if (!isEditMode) {
      setIsLoadingDetail(false);
      return;
    }

    const fetchChecklistDetail = async () => {
      setIsLoadingDetail(true);
      setDetailError("");

      try {
        const response = await axiosInstance.get(`/api/checklist/${checklist_id}`);
        const data = response.data || {};

        const sortByDisplayOrderIfAvailable = (list) => {
          const safeList = Array.isArray(list) ? list : [];
          return [...safeList].sort((a, b) => {
            const aOrder = Number.isFinite(Number(a?.display_order))
              ? Number(a.display_order)
              : null;
            const bOrder = Number.isFinite(Number(b?.display_order))
              ? Number(b.display_order)
              : null;
            if (aOrder === null || bOrder === null) return 0;
            return aOrder - bOrder;
          });
        };

        const sortedSections = sortByDisplayOrderIfAvailable(data.sections);

        const normalizedSections = sortedSections.map((section) => {
          const sortedItems = sortByDisplayOrderIfAvailable(section.items);

          return {
            id: section?.id || "",
            name: section?.name || "",
            requirement_type: section?.requirement_type || "ALL_REQUIRED",
            section_note: section?.section_note || "",
            items:
              sortedItems.length > 0
                ? sortedItems.map((item) => ({
                    id: item?.id || "",
                    item_name: item?.item_name || "",
                    item_note: item?.item_note || "",
                  }))
                : [createEmptyItem()],
          };
        });

        setFormData({
          checklist_name: data?.checklist_name || "",
          checklist_description: data?.checklist_description || "",
          status: data?.status === "draft" ? "draft" : "publish",
          sections:
            normalizedSections.length > 0
              ? normalizedSections
              : [createEmptySection()],
        });
      } catch (error) {
        console.error("Error loading checklist detail:", error);
        setDetailError(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to load checklist details"
        );
      } finally {
        setIsLoadingDetail(false);
      }
    };

    fetchChecklistDetail();
  }, [checklist_id, isEditMode]);

  const validateForm = () => {
    const nextErrors = {
      checklist_name: "",
      sections: [],
    };
    let hasError = false;

    if (!formData.checklist_name.trim()) {
      nextErrors.checklist_name = "Template Name is required";
      hasError = true;
    }

    if (!Array.isArray(formData.sections) || formData.sections.length === 0) {
      nextErrors.sections = [];
      hasError = true;
    } else {
      nextErrors.sections = formData.sections.map((section) => {
        const sectionError = {
          name: "",
          items: "",
          itemNames: [],
        };

        if (!section.name.trim()) {
          sectionError.name = "Section Name is required";
          hasError = true;
        }

        if (!Array.isArray(section.items) || section.items.length === 0) {
          sectionError.items = "At least one item is required";
          hasError = true;
        } else {
          sectionError.itemNames = section.items.map((item) => {
            if (!item.item_name.trim()) {
              hasError = true;
              return "Item Name is required";
            }
            return "";
          });
        }

        return sectionError;
      });
    }

    if (formData.sections.length === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("At least one section is required");
      setSnackbarOpen(true);
    }

    setErrors(nextErrors);
    return !hasError;
  };

  const handleTopLevelChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "checklist_name") {
      setErrors((prev) => ({
        ...prev,
        checklist_name: "",
      }));
    }
  };

  const addSection = () => {
    setFormData((prev) => ({
      ...prev,
      sections: [...prev.sections, createEmptySection()],
    }));
    setErrors((prev) => ({
      ...prev,
      sections: [...(prev.sections || []), { name: "", items: "", itemNames: [] }],
    }));
  };

  const removeSection = (sectionIndex) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, idx) => idx !== sectionIndex),
    }));
    setErrors((prev) => ({
      ...prev,
      sections: (prev.sections || []).filter((_, idx) => idx !== sectionIndex),
    }));
  };

  // Nested section update while preserving sibling sections and items.
  const updateSectionField = (sectionIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, idx) =>
        idx === sectionIndex ? { ...section, [field]: value } : section
      ),
    }));

    setErrors((prev) => {
      const nextSections = [...(prev.sections || [])];
      if (!nextSections[sectionIndex]) {
        nextSections[sectionIndex] = { name: "", items: "", itemNames: [] };
      }

      if (field === "name") {
        nextSections[sectionIndex].name = "";
      }
      if (field === "requirement_type") {
        nextSections[sectionIndex].items = nextSections[sectionIndex].items || "";
      }

      return {
        ...prev,
        sections: nextSections,
      };
    });
  };

  const addItem = (sectionIndex) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, idx) =>
        idx === sectionIndex
          ? { ...section, items: [...section.items, createEmptyItem()] }
          : section
      ),
    }));
    setErrors((prev) => {
      const nextSections = [...(prev.sections || [])];
      if (!nextSections[sectionIndex]) {
        nextSections[sectionIndex] = { name: "", items: "", itemNames: [] };
      }
      nextSections[sectionIndex].items = "";
      return {
        ...prev,
        sections: nextSections,
      };
    });
  };

  const removeItem = (sectionIndex, itemIndex) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, idx) =>
        idx === sectionIndex
          ? {
              ...section,
              items: section.items.filter((_, itemIdx) => itemIdx !== itemIndex),
            }
          : section
      ),
    }));

    setErrors((prev) => {
      const nextSections = [...(prev.sections || [])];
      if (!nextSections[sectionIndex]) return prev;

      nextSections[sectionIndex] = {
        ...nextSections[sectionIndex],
        itemNames: (nextSections[sectionIndex].itemNames || []).filter(
          (_, idx) => idx !== itemIndex
        ),
      };

      return {
        ...prev,
        sections: nextSections,
      };
    });
  };

  // Nested item update with immutable deep state handling.
  const updateItemField = (sectionIndex, itemIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, idx) =>
        idx === sectionIndex
          ? {
              ...section,
              items: section.items.map((item, idxItem) =>
                idxItem === itemIndex ? { ...item, [field]: value } : item
              ),
            }
          : section
      ),
    }));

    if (field === "item_name") {
      setErrors((prev) => {
        const nextSections = [...(prev.sections || [])];
        if (!nextSections[sectionIndex]) {
          nextSections[sectionIndex] = { name: "", items: "", itemNames: [] };
        }
        const nextItemNames = [...(nextSections[sectionIndex].itemNames || [])];
        nextItemNames[itemIndex] = "";
        nextSections[sectionIndex].itemNames = nextItemNames;
        return {
          ...prev,
          sections: nextSections,
        };
      });
    }
  };

  const handleSave = async () => {
    if (isSaving) return;

    if (!validateForm()) {
      return;
    }

    // Payload mapping with display_order generated from current UI order.
    // In edit mode, preserve existing section/item ids so backend can update vs create.
    const payload = {
      checklist_name: formData.checklist_name.trim(),
      checklist_description: formData.checklist_description.trim(),
      status: statusMap[formData.status] || "publish",
      sections: formData.sections.map((section, sectionIndex) => {
        const mappedSection = {
          name: section.name.trim(),
          requirement_type: section.requirement_type,
          section_note: section.section_note.trim(),
          display_order: sectionIndex + 1,
          items: section.items.map((item, itemIndex) => {
            const mappedItem = {
              item_name: item.item_name.trim(),
              item_note: item.item_note.trim(),
              display_order: itemIndex + 1,
            };

            if (item.id) {
              mappedItem.id = item.id;
            }

            return mappedItem;
          }),
        };

        if (section.id) {
          mappedSection.id = section.id;
        }

        return mappedSection;
      }),
    };

    try {
      setIsSaving(true);

      if (isEditMode) {
        const response = await axiosInstance.put(
          `/api/checklist/${checklist_id}`,
          payload
        );

        setSnackbarSeverity("success");
        setSnackbarMessage(
          response.data?.message || "Checklist template updated successfully"
        );
        setSnackbarOpen(true);

        setTimeout(() => {
          navigate("/checklist");
        }, 1200);
        return;
      }

      await axiosInstance.post("/api/create_checklist", payload);

      setSnackbarSeverity("success");
      setSnackbarMessage("Checklist template created successfully");
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate("/checklist");
      }, 1200);
    } catch (error) {
      console.error("Error saving checklist template:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          (isEditMode
            ? "Failed to update checklist template"
            : "Failed to create checklist template")
      );
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    navigate("/checklist");
  };

  return (
    <div
      className={`table-container ${isSidebarExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="table-container-content checklist-form-page">
        <div className="table-header">
          <div className="table-header-content checklist-form-header">
            <div className="table-left-side">
              <h2 className="jost-bold">{pageTitle}</h2>
            </div>
            <div className="table-right-side">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-save checklist-save-btn"
                onClick={handleSave}
                disabled={isSaving || isLoadingDetail}
              >
                {saveButtonLabel}
              </button>
            </div>
          </div>
        </div>

        <div className="checklist-form-body">
          {isLoadingDetail ? (
            <div className="checklist-card">
              <p className="checklist-empty-state">Loading checklist details...</p>
            </div>
          ) : detailError ? (
            <div className="checklist-card">
              <p className="error-message">{detailError}</p>
            </div>
          ) : (
            <>
          <div className="checklist-card">
            <h3 className="checklist-card-title">Template Details</h3>
            <div className="checklist-grid two-col">
              <div className="checklist-field">
                <label className="form-label">
                  Template Name<span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field checklist-input-full"
                  placeholder="Enter template name"
                  value={formData.checklist_name}
                  onChange={(event) =>
                    handleTopLevelChange("checklist_name", event.target.value)
                  }
                />
                {errors.checklist_name ? (
                  <p className="error-message">{errors.checklist_name}</p>
                ) : null}
              </div>

              <div className="checklist-field">
                <label className="form-label">Status</label>
                <select
                  className="input-field checklist-input-full"
                  value={formData.status}
                  onChange={(event) =>
                    handleTopLevelChange("status", event.target.value)
                  }
                >
                  <option value="publish">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            <div className="checklist-grid one-col">
              <div className="checklist-field">
                <label className="form-label">Subheading / Description</label>
                <textarea
                  className="input-field checklist-input-full checklist-textarea"
                  placeholder="Enter checklist description"
                  value={formData.checklist_description}
                  onChange={(event) =>
                    handleTopLevelChange(
                      "checklist_description",
                      event.target.value
                    )
                  }
                />
              </div>
            </div>
          </div>

          <div className="checklist-card">
            <div className="checklist-sections-head">
              <h3 className="checklist-card-title">
                Sections ({formData.sections.length})
              </h3>
              <button
                type="button"
                className="new-button checklist-add-btn"
                onClick={addSection}
              >
                <span className="new-icon">
                  <LuPlus />
                </span>
                <span>Add Section</span>
              </button>
            </div>

            {formData.sections.length === 0 ? (
              <p className="checklist-empty-state">
                No sections added yet. Click Add Section.
              </p>
            ) : null}

            {formData.sections.map((section, sectionIndex) => (
              <div className="checklist-section-card" key={`section-${sectionIndex}`}>
                <div className="checklist-section-header">
                  <div className="checklist-section-index">
                    Section {sectionIndex + 1}
                  </div>
                  <button
                    type="button"
                    className="checklist-delete-btn"
                    onClick={() => removeSection(sectionIndex)}
                  >
                    <CiTrash />
                    <span>Delete Section</span>
                  </button>
                </div>

                <div className="checklist-grid two-col">
                  <div className="checklist-field">
                    <label className="form-label">
                      Section Name<span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="input-field checklist-input-full"
                      placeholder="Enter section name"
                      value={section.name}
                      onChange={(event) =>
                        updateSectionField(sectionIndex, "name", event.target.value)
                      }
                    />
                    {errors.sections?.[sectionIndex]?.name ? (
                      <p className="error-message">
                        {errors.sections[sectionIndex].name}
                      </p>
                    ) : null}
                  </div>

                  <div className="checklist-field">
                    <label className="form-label">Requirement Type</label>
                    <select
                      className="input-field checklist-input-full"
                      value={section.requirement_type}
                      onChange={(event) =>
                        updateSectionField(
                          sectionIndex,
                          "requirement_type",
                          event.target.value
                        )
                      }
                    >
                      <option value="ALL_REQUIRED">All Required</option>
                      <option value="ANY_ONE">Any One</option>
                    </select>
                  </div>
                </div>

                <div className="checklist-grid one-col">
                  <div className="checklist-field">
                    <label className="form-label">Section Note</label>
                    <input
                      type="text"
                      className="input-field checklist-input-full"
                      placeholder="Enter section note"
                      value={section.section_note}
                      onChange={(event) =>
                        updateSectionField(
                          sectionIndex,
                          "section_note",
                          event.target.value
                        )
                      }
                    />
                  </div>
                </div>

                <div className="checklist-items-head">
                  <h4 className="checklist-items-title">Items</h4>
                  <button
                    type="button"
                    className="new-button checklist-add-btn checklist-add-item-btn"
                    onClick={() => addItem(sectionIndex)}
                  >
                    <span className="new-icon">
                      <LuPlus />
                    </span>
                    <span>Add Item</span>
                  </button>
                </div>

                {errors.sections?.[sectionIndex]?.items ? (
                  <p className="error-message checklist-items-error">
                    {errors.sections[sectionIndex].items}
                  </p>
                ) : null}

                <div className="checklist-items-wrap">
                  {section.items.map((item, itemIndex) => (
                    <div
                      className="checklist-item-card"
                      key={`section-${sectionIndex}-item-${itemIndex}`}
                    >
                      <div className="checklist-item-header">
                        <span className="checklist-item-index">
                          Item {itemIndex + 1}
                        </span>
                        <button
                          type="button"
                          className="checklist-delete-btn checklist-delete-item-btn"
                          onClick={() => removeItem(sectionIndex, itemIndex)}
                        >
                          <CiTrash />
                        </button>
                      </div>

                      <div className="checklist-grid two-col">
                        <div className="checklist-field">
                          <label className="form-label">
                            Item Name<span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            className="input-field checklist-input-full"
                            placeholder="Enter item name"
                            value={item.item_name}
                            onChange={(event) =>
                              updateItemField(
                                sectionIndex,
                                itemIndex,
                                "item_name",
                                event.target.value
                              )
                            }
                          />
                          {errors.sections?.[sectionIndex]?.itemNames?.[itemIndex] ? (
                            <p className="error-message">
                              {errors.sections[sectionIndex].itemNames[itemIndex]}
                            </p>
                          ) : null}
                        </div>

                        <div className="checklist-field">
                          <label className="form-label">Item Note</label>
                          <input
                            type="text"
                            className="input-field checklist-input-full"
                            placeholder="Enter item note"
                            value={item.item_note}
                            onChange={(event) =>
                              updateItemField(
                                sectionIndex,
                                itemIndex,
                                "item_note",
                                event.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
            </>
          )}
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}

export default ChecklistTemplateForm;
